import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useDrawer } from "../../context/DrawerContext";
import { easing } from "../../lib/animations";
import { MonoLabel } from "./primitives";

const NAV = [
    { id: "shop", label: "Lookbook", to: "/shop" },
    { id: "about", label: "Manifest", to: "/about" },
    { id: "drops", label: "Drops", to: "/drop/field-002" },
    { id: "cart", label: "Cart", to: "/cart" },
    { id: "account", label: "My Account", to: "/account" },
    { id: "contact", label: "Contact", to: "/contact" },
];

const SECONDARY = [
    { id: "shipping", label: "Shipping", to: "/shipping" },
    { id: "returns", label: "Returns", to: "/policy/returns" },
    { id: "faq", label: "FAQ", to: "/policy/faq" },
];

/**
 * MobileDrawer — fullscreen brutalist overlay.
 * Mounted at App-root so it escapes Header's mix-blend-mode stacking context.
 * Reads its open state from DrawerContext (any component can `useDrawer()`).
 */
export const MobileDrawer = () => {
    const { open, closeDrawer } = useDrawer();
    const location = useLocation();
    const { count } = useCart();
    const touchStartX = React.useRef(null);

    // Close drawer on route change
    useEffect(() => {
        if (open) closeDrawer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Body scroll lock
    useEffect(() => {
        if (open) document.body.classList.add("menu-open");
        else document.body.classList.remove("menu-open");
        return () => document.body.classList.remove("menu-open");
    }, [open]);

    // Touch swipe-to-close — left swipe dismisses
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (dx < -70) closeDrawer();
    };

    const onClose = closeDrawer;

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop — semi-transparent overlay for the area not covered by the drawer */}
                    <motion.div
                        key="drawer-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: easing.primary }}
                        onClick={onClose}
                        data-testid="mobile-drawer-backdrop"
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 290,
                            background: "rgba(12,12,12,0.55)",
                            backdropFilter: "blur(2px)",
                            WebkitBackdropFilter: "blur(2px)",
                        }}
                    />
                    <motion.div
                        key="drawer"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.42, ease: easing.primary }}
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                        data-testid="mobile-drawer"
                        style={{
                            position: "fixed",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: "min(420px, 88vw)",
                            zIndex: 300,
                            background: "var(--ink)",
                            color: "var(--on-ink)",
                            display: "flex",
                            flexDirection: "column",
                            padding: "clamp(24px, 6vw, 36px)",
                            overflowY: "auto",
                            boxShadow: "-16px 0 48px rgba(0,0,0,0.45)",
                        }}
                    >
                    {/* TOP BAR */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "clamp(36px, 8vw, 52px)",
                        }}
                    >
                        <Link
                            to="/"
                            onClick={onClose}
                            data-testid="drawer-logo"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                color: "var(--on-ink)",
                                textDecoration: "none",
                            }}
                        >
                            <img
                                src="/assets/yk/sygnet-white.png"
                                alt="PROOF"
                                style={{
                                    height: 34,
                                    width: "auto",
                                }}
                            />
                        </Link>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="drawer-close"
                            aria-label="Close menu"
                            style={{
                                width: 42,
                                height: 42,
                                background: "transparent",
                                border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.45))",
                                color: "var(--on-ink)",
                                cursor: "pointer",
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                aria-hidden="true"
                            >
                                <line x1="2" y1="2" x2="16" y2="16" stroke="currentColor" />
                                <line x1="16" y1="2" x2="2" y2="16" stroke="currentColor" />
                            </svg>
                        </button>
                    </div>

                    {/* PRIMARY NAV */}
                    <nav
                        aria-label="Primary mobile"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            borderTop: "1px solid var(--on-ink-line)",
                        }}
                    >
                        {NAV.map((n, i) => {
                            const active =
                                n.to === location.pathname ||
                                (n.to !== "/" && location.pathname.startsWith(n.to + "/"));
                            return (
                                <motion.div
                                    key={n.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 0.08 + i * 0.05,
                                        ease: easing.primary,
                                    }}
                                >
                                    <Link
                                        to={n.to}
                                        onClick={onClose}
                                        data-testid={`drawer-link-${n.id}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            justifyContent: "space-between",
                                            gap: 18,
                                            padding: "18px 0",
                                            borderBottom: "1px solid var(--on-ink-line)",
                                            color: active
                                                ? "var(--on-ink)"
                                                : "var(--on-ink-soft)",
                                            textDecoration: "none",
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 600,
                                            fontSize: "clamp(28px, 7vw, 44px)",
                                            letterSpacing: "-0.025em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        <span>
                                            <span
                                                style={{
                                                    fontFamily:
                                                        "var(--font-mono)",
                                                    fontSize: 11,
                                                    letterSpacing: "0.28em",
                                                    textTransform: "uppercase",
                                                    color: "var(--on-ink-mute)",
                                                    marginRight: 14,
                                                    fontWeight: 400,
                                                }}
                                            >
                                                0{i + 1}
                                            </span>
                                            {n.label}
                                        </span>
                                        {n.id === "cart" && count > 0 && (
                                            <span
                                                data-testid="drawer-cart-count"
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "center",
                                                    minWidth: 26,
                                                    height: 26,
                                                    padding: "0 8px",
                                                    background: "var(--signal)",
                                                    color: "#fff",
                                                    fontFamily:
                                                        "var(--font-mono)",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {count}
                                            </span>
                                        )}
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </nav>

                    {/* SECONDARY NAV */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.45, ease: easing.primary }}
                        style={{
                            marginTop: "auto",
                            paddingTop: "clamp(24px, 6vw, 40px)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                        }}
                    >
                        <MonoLabel tone="mute">— Field protocol</MonoLabel>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 18,
                            }}
                        >
                            {SECONDARY.map((s) => (
                                <Link
                                    key={s.id}
                                    to={s.to}
                                    onClick={onClose}
                                    data-testid={`drawer-link-${s.id}`}
                                    style={{
                                        color: "var(--on-ink-soft)",
                                        textDecoration: "none",
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 11,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {s.label}
                                </Link>
                            ))}
                        </div>
                        <MonoLabel
                            tone="faint"
                            style={{ marginTop: 8 }}
                        >
                            © {new Date().getFullYear()} Proof · Warsaw
                        </MonoLabel>
                    </motion.div>
                </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default MobileDrawer;
